import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { getAppDetails } from "../services/api";
import { Download, ArrowLeft, ShieldCheck, X, ChevronLeft, ChevronRight } from "lucide-react";
import Logo from "../assets/quivix.png";

const getAppImage = (app) =>
  app?.image || app?.icon_url || app?.cover_image || Logo;

const normalizeScreenshot = (screenshot) => {
  if (typeof screenshot === "string") {
    return { id: screenshot, image_url: screenshot, caption: "" };
  }
  return {
    ...screenshot,
    id: screenshot?.id || screenshot?.image_url || screenshot?.url || screenshot?.image,
    image_url: screenshot?.image_url || screenshot?.url || screenshot?.image || "",
    caption: screenshot?.caption || "",
  };
};

const Lightbox = ({ images, startIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const goToPrev = useCallback(
    () => setCurrentIndex((i) => (i === 0 ? images.length - 1 : i - 1)),
    [images.length]
  );
  const goToNext = useCallback(
    () => setCurrentIndex((i) => (i + 1) % images.length),
    [images.length]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") goToPrev();
      if (e.key === "ArrowRight") goToNext();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToPrev, goToNext, onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-2xl flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-4xl w-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        <img
          src={images[currentIndex].image_url}
          alt={images[currentIndex].caption || `Screenshot ${currentIndex + 1}`}
          className="max-h-[80vh] w-auto object-contain rounded-2xl border border-white/20 shadow-2xl"
        />
        <button onClick={onClose} className="absolute -top-12 right-0 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all">
          <X className="w-6 h-6" />
        </button>
        <button onClick={goToPrev} className="absolute left-2 text-white/80 hover:text-white p-3 rounded-full bg-black/60 border border-white/10 backdrop-blur-md transition-all">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button onClick={goToNext} className="absolute right-2 text-white/80 hover:text-white p-3 rounded-full bg-black/60 border border-white/10 backdrop-blur-md transition-all">
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

// --- Constants for Grid Calculation ---
// These must match the values in AppDetailPage.css for the logic to work.
const GRID_ROW_HEIGHT = 200; // From grid-auto-rows: 200px
const GRID_GAP = 24; // From gap: 1.5rem (1.5 * 16px)

// --- Intelligent Layout Calculation Function ---
// This function pre-loads an image to calculate its exact size,
// determining its orientation and how many grid rows it needs to span.
const getImageLayoutInfo = (url, columnWidthEstimate) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const { naturalWidth, naturalHeight } = img;

      if (naturalWidth >= naturalHeight) {
        // Landscape images are simple.
        resolve({ orientation: "landscape", span: 1 });
      } else {
        // Portrait images require height calculation.
        const aspectRatio = naturalHeight / naturalWidth;
        const requiredHeight = aspectRatio * columnWidthEstimate;
        // Calculate rows needed, including the gap between rows.
        const requiredRows = requiredHeight / (GRID_ROW_HEIGHT + GRID_GAP);
        const span = Math.ceil(requiredRows); // Round up to ensure it fits.
        resolve({ orientation: "portrait", span });
      }
    };
    img.onerror = () => resolve({ orientation: "landscape", span: 1 });
    img.src = url;
  });
};

// --- Individual Screenshot Item Component ---
// eslint-disable-next-line no-unused-vars
const ScreenshotItem = ({ screenshot, onOpen }) => {
  const orientationClass = `screenshot-item--${screenshot.orientation}`;
  // Dynamically set the grid-row style ONLY for portrait images.
  const style =
    screenshot.orientation === "portrait"
      ? { gridRow: `span ${screenshot.span || 2}` }
      : {};

  return (
    <div
      className={`screenshot-item ${orientationClass}`}
      style={style}
      onClick={onOpen}
    >
      <img
        src={screenshot.image_url}
        alt={screenshot.caption || "App screenshot"}
      />
    </div>
  );
};

// === Main Page Component ===
const AppDetailPage = () => {
  const { appId } = useParams();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxStartIndex, setLightboxStartIndex] = useState(0);

  useEffect(() => {
    const fetchAppDetails = async () => {
      try {
        setLoading(true);
        const response = await getAppDetails(appId);
        const appData = response.data;

        // Process screenshots to add layout info before setting state.
        if (appData && appData.screenshots) {
          const columnWidthEstimate = 280; // A reasonable estimate based on CSS.
          const screenshotsWithLayout = await Promise.all(
            appData.screenshots.map(async (rawScreenshot) => {
              const ss = normalizeScreenshot(rawScreenshot);
              const layout = await getImageLayoutInfo(
                ss.image_url,
                columnWidthEstimate
              );
              return { ...ss, ...layout };
            })
          );
          appData.screenshots = screenshotsWithLayout;
        }

        setApp(appData);
        document.title = `${appData.title} - QuivixDigital`;
      } catch (err) {
        setError("Could not load app details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchAppDetails();
  }, [appId]);

  const openLightbox = (index) => {
    setLightboxStartIndex(index);
    setIsLightboxOpen(true);
  };

  if (loading) return <div className="loading-spinner"></div>;
  if (error) return <p className="error-message">{error}</p>;
  if (!app) return <p className="error-message">App not found.</p>;

  return (
    <div className="bg-black text-[#f5f5f7] min-h-screen pt-36 pb-24 font-sans selection:bg-blue-500/30">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        
        {/* Back navigation */}
        <Link to="/apps" className="inline-flex items-center gap-2 text-xs font-mono text-[#86868b] hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Ecosystem
        </Link>

        {/* Hero Header Box */}
        <div className="apple-glass rounded-3xl p-8 sm:p-12 border border-white/[0.08] relative overflow-hidden mb-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 relative z-10">
            <div className="flex items-start sm:items-center gap-6">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-neutral-900 border border-white/15 overflow-hidden flex items-center justify-center shrink-0 shadow-2xl">
                <img src={getAppImage(app)} alt={app.title} className="w-full h-full object-cover" />
              </div>

              <div>
                <span className="text-[10px] font-mono text-blue-400 uppercase tracking-widest bg-blue-500/10 px-2.5 py-0.5 rounded-full border border-blue-500/20">
                  {app.category || "QuivixDigital Software"}
                </span>
                <h1 className="text-3xl sm:text-5xl font-bold text-white mt-2 font-heading">{app.title}</h1>
                <p className="text-xs text-[#86868b] font-mono mt-1">Engineered by {app.developer || "QuivixDigital"}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {app.download_url && (
                <a
                  href={app.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-black font-semibold text-xs hover:bg-neutral-200 transition-all shadow-[0_0_30px_rgba(255,255,255,0.2)] active:scale-95"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Product</span>
                </a>
              )}
            </div>
          </div>

          {/* Key Metrics Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 pt-8 border-t border-white/[0.08]">
            <div>
              <span className="text-[11px] font-mono text-[#86868b] uppercase">Active Installs</span>
              <div className="text-xl font-bold text-white font-heading mt-1">{app.downloads ? `${app.downloads}+` : "100K+"}</div>
            </div>
            <div>
              <span className="text-[11px] font-mono text-[#86868b] uppercase">System Version</span>
              <div className="text-xl font-bold text-white font-heading mt-1">{app.version || "3.2.0 Pro"}</div>
            </div>
            <div>
              <span className="text-[11px] font-mono text-[#86868b] uppercase">Security Audit</span>
              <div className="text-xl font-bold text-emerald-400 font-heading mt-1 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" /> Verified
              </div>
            </div>
            <div>
              <span className="text-[11px] font-mono text-[#86868b] uppercase">Last Released</span>
              <div className="text-xl font-bold text-white font-heading mt-1">{new Date(app.updated_at || Date.now()).toLocaleDateString()}</div>
            </div>
          </div>
        </div>

        {/* Screenshots Showcase */}
        {app.screenshots?.length > 0 && (
          <div className="mb-16">
            <h2 className="text-xl font-bold text-white font-heading mb-6">Interface Gallery</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {app.screenshots.map((ss, index) => (
                <div
                  key={ss.id || index}
                  onClick={() => openLightbox(index)}
                  className="apple-glass rounded-2xl p-2 border border-white/[0.08] apple-card-hover cursor-pointer overflow-hidden group"
                >
                  <img
                    src={ss.image_url}
                    alt={ss.caption || "App Screenshot"}
                    className="w-full h-56 object-cover rounded-xl group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* About App */}
        <div className="apple-glass rounded-3xl p-8 sm:p-12 border border-white/[0.08]">
          <h2 className="text-2xl font-bold text-white font-heading mb-4">Product Specifications & Overview</h2>
          <p className="text-sm text-[#86868b] leading-relaxed whitespace-pre-line">{app.description}</p>
        </div>

      </div>

      {isLightboxOpen && (
        <Lightbox
          images={app.screenshots}
          startIndex={lightboxStartIndex}
          onClose={() => setIsLightboxOpen(false)}
        />
      )}
    </div>
  );
};

export default AppDetailPage;
